/**
 * Status badge shared between Google `CampaignCard` and `MetaCampaignCard`.
 *
 * Renders any of the recognized campaign-status vocabulary values with a
 * consistent color scheme. Google uses `ENABLED | PAUSED | REMOVED`;
 * Meta uses `ACTIVE | PAUSED | ARCHIVED | DELETED`. Both pass through
 * here.
 *
 * Label casing matches the rest of the app via `formatEnumPretty` —
 * `ENABLED → "Enabled"`, `ACTIVE → "Active"`, etc. Color matching is
 * done on the uppercased vocabulary because both platforms emit the
 * raw upper form.
 */
import { cn } from '@/utils/cn';
import { formatEnumPretty, stripEnumPrefix } from '@/utils/formatEnum';

const STATUS_BADGE_STYLES: Record<string, string> = {
  // Google
  ENABLED: 'bg-green-100 text-green-800',
  REMOVED: 'bg-red-100 text-red-700',
  // Meta
  ACTIVE: 'bg-green-100 text-green-800',
  ARCHIVED: 'bg-gray-100 text-gray-700',
  DELETED: 'bg-red-100 text-red-700',
  // Shared
  PAUSED: 'bg-yellow-100 text-yellow-800',
};

interface CampaignStatusBadgeProps {
  status: string | null | undefined;
  /** Optional CSS class additions (e.g. spacing inside the card header). */
  className?: string;
}

export function CampaignStatusBadge({ status, className }: CampaignStatusBadgeProps) {
  if (!status) return null;
  // Strip any `CampaignStatus.` / `AdGroupStatus.` etc. prefix BEFORE
  // uppercasing so the palette lookup keys on the bare vocabulary
  // value the map is built around. Without this, a backend that ever
  // sends `"CampaignStatus.ENABLED"` (a shape that exists elsewhere
  // in the codebase — see other `stripEnumPrefix` call sites) would
  // prettify the label correctly but fall through to gray for color
  // because `"CAMPAIGNSTATUS.ENABLED"` doesn't match any key.
  const normalized = stripEnumPrefix(status).toUpperCase();
  const palette = STATUS_BADGE_STYLES[normalized] ?? 'bg-gray-100 text-gray-600';
  const label = formatEnumPretty(status);
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium flex-shrink-0',
        palette,
        className,
      )}
    >
      {label}
    </span>
  );
}
